import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  message,
  Popconfirm,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import AuthGuard from '../components/AuthGuard';
import {
  createAdmin,
  getAdminList,
  updateAdmin,
  deleteAdmin,
} from '../services/adminApi';

const { Option } = Select;
const { Search } = Input;

interface AdminUser {
  id: number;
  phone: string;
  nickname: string;
  role: string;
  isSuperAdmin: boolean;
  status: 'active' | 'inactive' | 'suspended';
  referralCode: string;
  createdAt: string;
  updatedAt: string;
}

const AdminManagement: React.FC = () => {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<AdminUser | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [superAdminFilter, setSuperAdminFilter] = useState<boolean | undefined>();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // 获取管理员列表
  const fetchAdmins = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await getAdminList({
        page,
        pageSize,
        keyword: searchText || undefined,
        isSuperAdmin: superAdminFilter,
      });

      if (response.data.success) {
        setAdmins(response.data.data.list);
        setPagination({
          current: response.data.data.page,
          pageSize: response.data.data.pageSize,
          total: response.data.data.total,
        });
      }
    } catch (error: any) {
      message.error(error.message || '获取管理员列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, [searchText, superAdminFilter]);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '昵称',
      dataIndex: 'nickname',
      key: 'nickname',
    },
    {
      title: '角色',
      dataIndex: 'isSuperAdmin',
      key: 'isSuperAdmin',
      render: (isSuperAdmin: boolean) => (
        <Tag color={isSuperAdmin ? 'red' : 'blue'}>
          {isSuperAdmin ? '超级管理员' : '普通管理员'}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: Record<string, { label: string; color: string }> = {
          active: { label: '活跃', color: 'success' },
          inactive: { label: '非活跃', color: 'default' },
          suspended: { label: '已暂停', color: 'error' },
        };
        const statusInfo = statusMap[status] || statusMap.active;
        return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>;
      },
    },
    {
      title: '邀请码',
      dataIndex: 'referralCode',
      key: 'referralCode',
      render: (code: string) => <code>{code}</code>,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: any, record: AdminUser) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={record.isSuperAdmin}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个管理员吗？"
            description="删除后该管理员将无法登录系统"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            disabled={record.isSuperAdmin}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              disabled={record.isSuperAdmin}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingAdmin(null);
    setModalVisible(true);
    form.resetFields();
  };

  const handleEdit = (admin: AdminUser) => {
    setEditingAdmin(admin);
    setModalVisible(true);
    form.setFieldsValue({
      nickname: admin.nickname,
      status: admin.status,
    });
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await deleteAdmin(id);
      if (response.data.success) {
        message.success('删除成功');
        fetchAdmins(pagination.current, pagination.pageSize);
      }
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingAdmin) {
        // 更新管理员
        const response = await updateAdmin(editingAdmin.id, {
          nickname: values.nickname,
          status: values.status,
        });

        if (response.data.success) {
          message.success('更新成功');
          setModalVisible(false);
          fetchAdmins(pagination.current, pagination.pageSize);
        }
      } else {
        // 创建管理员
        const response = await createAdmin({
          phone: values.phone,
          password: values.password,
          nickname: values.nickname,
          isSuperAdmin: values.isSuperAdmin,
        });

        if (response.data.success) {
          message.success('创建成功');
          setModalVisible(false);
          fetchAdmins(pagination.current, pagination.pageSize);
        }
      }
    } catch (error: any) {
      message.error(error.message || '操作失败');
    }
  };

  const handleTableChange = (pagination: any) => {
    fetchAdmins(pagination.current, pagination.pageSize);
  };

  return (
    <AuthGuard requireSuperAdmin>
      <div>
        <Card
          title="管理员管理"
          extra={
            <Space>
              <Button icon={<ReloadOutlined />} onClick={() => fetchAdmins()}>
                刷新
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                新增管理员
              </Button>
            </Space>
          }
        >
          <div style={{ marginBottom: '16px' }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={8}>
                <Search
                  placeholder="搜索手机号或昵称"
                  onSearch={setSearchText}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="筛选角色"
                  allowClear
                  value={superAdminFilter}
                  onChange={setSuperAdminFilter}
                  style={{ width: '100%' }}
                >
                  <Option value={true}>超级管理员</Option>
                  <Option value={false}>普通管理员</Option>
                </Select>
              </Col>
            </Row>
          </div>

          <Table
            columns={columns}
            dataSource={admins}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
            }}
            onChange={handleTableChange}
          />
        </Card>

        <Modal
          title={editingAdmin ? '编辑管理员' : '新增管理员'}
          open={modalVisible}
          onOk={handleModalOk}
          onCancel={() => setModalVisible(false)}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              status: 'active',
              isSuperAdmin: false,
            }}
          >
            {!editingAdmin && (
              <>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="phone"
                      label="手机号"
                      rules={[
                        { required: true, message: '请输入手机号' },
                        { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' },
                      ]}
                    >
                      <Input placeholder="请输入手机号" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="password"
                      label="密码"
                      rules={[
                        { required: true, message: '请输入密码' },
                        { min: 6, message: '密码至少6位' },
                      ]}
                    >
                      <Input.Password placeholder="请输入密码" />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item
                  name="isSuperAdmin"
                  label="角色"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="超级管理员" unCheckedChildren="普通管理员" />
                </Form.Item>
              </>
            )}

            <Form.Item
              name="nickname"
              label="昵称"
              rules={[{ required: true, message: '请输入昵称' }]}
            >
              <Input placeholder="请输入昵称" />
            </Form.Item>

            {editingAdmin && (
              <Form.Item
                name="status"
                label="状态"
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select placeholder="请选择状态">
                  <Option value="active">活跃</Option>
                  <Option value="inactive">非活跃</Option>
                  <Option value="suspended">已暂停</Option>
                </Select>
              </Form.Item>
            )}
          </Form>
        </Modal>
      </div>
    </AuthGuard>
  );
};

export default AdminManagement;